import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { PaginationResult } from '@/lib/pagination';
import { DocumentService } from '@/services/api/DocumentService';
import { DocumentFilters, DocumentWithMetadata } from '@/types/knowledge/knowledge-base';

export const usePaginatedDocuments = (
  viewMode: "my-documents" | "all-documents" = "my-documents",
  pageSize: number = 20,
  filters: DocumentFilters = {},
  userId?: string,
) => {
  return useInfiniteQuery({
    queryKey: [
      "documents-infinite",
      userId,
      viewMode,
      pageSize,
      filters.searchTerm,
      filters.selectedUsername,
      filters.selectedPrivacy,
      filters.folderId,
      filters.agentId,
      filters.selectedActivityStatus,
      filters.selectedProcessingStatus,
      filters.selectedSourceType,
    ],
    queryFn: async ({ pageParam = 1 }): Promise<PaginationResult<DocumentWithMetadata>> => {
      return await DocumentService.getKnowledgeBaseDocumentsPaginated(
        userId,
        pageParam,
        pageSize,
        filters,
        viewMode === "all-documents",
      );
    },
    getNextPageParam: (lastPage) => {
      return lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    initialPageParam: 1,
  });
};

export const useDocuments = (
  viewMode: "my-documents" | "all-documents" = "my-documents",
  filters: DocumentFilters = {},
  userId?: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [
      "documents",
      userId,
      viewMode,
      filters.searchTerm,
      filters.selectedUsername,
      filters.selectedPrivacy,
      filters.folderId,
      filters.agentId,
      filters.selectedActivityStatus,
      filters.selectedProcessingStatus,
      filters.selectedSourceType,
    ],
    queryFn: async (): Promise<DocumentWithMetadata[]> => {
      const isAdmin = viewMode === "all-documents";
      const targetUserId = isAdmin ? userId : undefined;
      return await DocumentService.getKnowledgeBaseDocuments(filters, isAdmin, targetUserId);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled,
  });
};

export const useDocumentsList = (
  viewMode: "my-documents" | "all-documents" = "my-documents",
  folderId?: string | null,
) => {
  return useQuery({
    queryKey: ["knowledge-base-docs", viewMode, folderId],
    queryFn: async () => {
      const isAdmin = viewMode === "all-documents";
      const filters: DocumentFilters = {
        folderId: folderId !== undefined ? (folderId === null ? "null" : folderId) : undefined,
      };

      return await DocumentService.getDocumentsList(filters, isAdmin);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useDocumentsOutsideWorkspace = () => {
  return useQuery({
    queryKey: ["documents-outside-workspace"],
    queryFn: async (): Promise<DocumentWithMetadata[]> => {
      const result = await DocumentService.getDocumentsOutsideWorkspace(1, 50);
      return result.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useDocument = (documentId: string | null | undefined) => {
  return useQuery({
    queryKey: ["document", documentId],
    queryFn: async (): Promise<DocumentWithMetadata | null> => {
      if (!documentId) return null;
      return await DocumentService.getKnowledgeBaseDocument(documentId);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!documentId,
  });
};
